/**
 * @file Functions used in sorting project outlines.
 */

import moment from "moment";
import Project from "../../main/project.js";
import {COLOR_TYPES, TOPIC_COLORS} from "../../main/constants/colors.js";
import {
	account, accountUuid,
	annotation,
	annotationUuid,
	mergedOutline2, mergedOutlineTopic2,
	project,
	topic,
	topicUuid
} from "../../main/data/model/index.js";

interface sortedOutlineTemp {
	label:       string;
	render:      string;
	annotations: {
		annotation: annotation;
		topic: {
			topic_uuid?: topicUuid;
			type?:       string;
		};
	}[];
};

type sortedOutline = {
	topic_uuid?: topicUuid;
	type?:       string;
	annotations: {
		annotation: annotation;
		topic: {
			topic_uuid?: topicUuid;
			type?:       string;
		};
	}[];
}[];

export type virtualOutline = {
	annotations: {
		annotation:    annotation;
		createdByName: string;
		topic:         topic | specialTopic;
	}[];
	topic:       topic | specialTopic;
	type?:       string;
	topic_uuid?: topicUuid;
}[];

interface specialTopic {
	color: string;
	label: string;
	type:  string;
};

type getAnnotationFn = (annotationUuid: annotationUuid) => annotation | undefined;

type renderFn = (value: string) => string;

type getTopicFn = (topicUuid: (topicUuid | undefined), topicType: (string | undefined)) => topic | specialTopic | undefined;

export function getVirtualOutline(myAccount: account, project: project, outline: mergedOutline2, topics: topic[], sortBy: "topic" | "source" | "date" | "editor"): virtualOutline {
	// Renderers for topic/group headings
	const renders: Record<string, renderFn> = {
		date:  (date: string) => moment(date, "YYYY-MM-DD").format("MMMM D, YYYY"),
		topic: (key: string) => {
			let renderKey = key;
			const topic = topics.find(t => t.uuid === key);

			if (topic) renderKey = topic.label;
			else if (renderKey === "bookmarks") renderKey = "Bookmarks";

			return renderKey;
		},
		editor: (accountUuid: string) => {
			if (accountUuid === "__PROJECT_OWNER__") {
				// This is a special case where we use a weird placeholder to prevent collisions in sort keys.
				accountUuid = project.account_uuid;
			}

			return Project.getUserNameFromShare(accountUuid as accountUuid, myAccount.uuid, project);
		}
	};

	const topicsCache: Record<string, topic | specialTopic> = {};
	const getTopic: getTopicFn = (topicUuid, topicType) => {
		if (!topicUuid && !topicType)
			return undefined;
		const key = (topicUuid ?? topicType)!;

		if (!topicsCache[key]) {
			if (topicUuid) {
				const topic = topics.find(t => t.uuid === topicUuid);
				if (topic)
					topicsCache[key] = topic;
			} else if (topicType) {
				switch (topicType) {
					case "bookmarks":
						topicsCache[key] = {
							// @ts-ignore - TODO
							color: TOPIC_COLORS[COLOR_TYPES.DEFAULT].bookmarks,
							label: "Bookmarks",
							type:  "bookmarks"
						} as specialTopic;
						break;
				}
			}
		}

		return topicsCache[key];
	};

	const getAnnotation: getAnnotationFn = uuid => {
		for (const ot of outline) {
			for (const a of ot.annotations) {
				if (a.uuid === uuid)
					return a;
			}
		}
		return undefined;
	};

	const sortedOutline = sortOutline(sortBy, outline, getAnnotation, renders[sortBy]);

	return sortedOutline.map(section => {
		const topic = getTopic(section.topic_uuid, section.type);

		return {
			...section,
			topic,
			annotations: section.annotations
				.map(a => {
					const annotationTopic = getTopic(a.topic.topic_uuid, a.topic.type);
					if (!annotationTopic)
						console.error("Encountered topic from another project: " + a.topic.topic_uuid);

					const annotation = a.annotation;

					const currentAccountUuid = myAccount.uuid;
					// If created_by does not exist, assume that it is the project owner
					const createdByUuid = annotation.created_by ? annotation.created_by : project.account_uuid;
					const createdByName = Project.getUserNameFromShare(createdByUuid, currentAccountUuid, project);

					// Hydrate the annotation placeholder with the real data,
					// plus additional helpful things like the full topic data
					return {
						annotation: a.annotation,
						createdByName,
						topic:      annotationTopic
					};
				})
			// 	.filter(ot => !!ot.topic) // Exclude all annotations without topic
		};
	}) as virtualOutline; // TODO: can we remove the "as virtualOutline" part?
}

/**
 * Creates a virtual outline grouped by specified type
 *
 * @param {string}   type            topic or one of availableTypes
 * @param {array}    outline         Raw outline
 * @param {function} getAnnotation   Function to get annotation information
 * @param {function} render          Function to render the section label
 */
const sortOutline = function (
	type: "topic" | "source" | "date" | "editor" = "topic",
	outline: mergedOutline2,
	getAnnotation: getAnnotationFn,
	render: renderFn | undefined
): sortedOutline {
	if (type === "topic")
		return defaultOutlineSort(outline, getAnnotation, render);
	else
		return customOutlineSort(type, outline, getAnnotation, render);
};

/**************************************************
 *               Sorting definitions              *
 **************************************************/

// Functions for getting annotation/card keys by sort type
const keyByTypeMap: Record<string, (annotation: annotation) => string | null> = {
	source: annotation => annotation?.pageData?.pageTitle ?? null,
	date:   annotation => {
		const createdDt = annotation?.created_dt;

		if (createdDt) {
			const date = new Date(createdDt);
			return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
		}

		return null;
	},
	editor: (annotation: annotation) => {
		// special case '__PROJECT_OWNER__' is resolved for display in the "render function".
		// See getVirtualOutlineSelector() in components/project/outline/index/page.jsx
		return annotation?.created_by ?? "__PROJECT_OWNER__";
	}
};

// Helpful sorting function for strings and other things...
const defaultSort: (a: string | number | null, b: string | number | null) => number = (a, b) => {
	if (a === b)
		return 0;
	else if (typeof a === "string" && typeof b === "string")
		return a.localeCompare(b);
	else if (a !== null && b !== null)
		return a < b ? -1 : 1;
	else
		return 0;
};

// Functions for sorting groups of items in the outline (topics, dates, etc.)
const groupSortFnByType: Record<string, (a: any, b: any) => number> = {
	date: (a: {label: string}, b: {label: string}) => {
		return new Date(a.label).getTime() - new Date(b.label).getTime();
	},
	editor: (a: {render: string}, b: {render: string}) => {
		// Ideally we would always put "Me" at the top,
		// but without relying on a direct string match
		// we don't have an easy way to do that from here.
		return defaultSort(a.render, b.render);
	},
	default: (a, b) => defaultSort(a.label, b.label)
};

// Functions for sorting contents of group items (so, individual annotations/cards within a group)
const contentsSortFnByType = (cacheAnnotations: Record<annotationUuid, annotation>): Record<string, ((a: {annotation: annotation}, b: {annotation: annotation}) => number)> => {
	const dateSort = (a: {annotation: annotation}, b: {annotation: annotation}) => {
		// Within date groupings, sort cards by created_dt

		const A = cacheAnnotations[a.annotation.uuid];
		const B = cacheAnnotations[b.annotation.uuid];

		const keyA = A?.created_dt ?? null;
		const keyB = B?.created_dt ?? null;

		return defaultSort(keyA, keyB);
	};

	return {
		date:   dateSort,
		editor: dateSort
	};
};

function customOutlineSort(
	type: "source" | "date" | "editor",
	outline: mergedOutline2,
	getAnnotation: (uuid: annotationUuid) => annotation | undefined,
	render: renderFn | undefined
): sortedOutline {
	const getKey = keyByTypeMap[type] || (() => null);
	const sortFn = groupSortFnByType[type] || groupSortFnByType["default"];

	const cacheAnnotations: Record<annotationUuid, annotation> = {};

	const output: sortedOutline = Object.values(outline.reduce((acc: Record<string, sortedOutlineTemp>, entry: mergedOutlineTopic2) => {
		// @ts-ignore - TODO
		const {topic_uuid, type, annotations} = entry;

		annotations.forEach(annotation => {
			const {uuid: annotation_uuid} = annotation;

			if (!cacheAnnotations[annotation_uuid]) {
				const a = getAnnotation(annotation_uuid);
				if (a)
					cacheAnnotations[annotation_uuid] = a;
			}

			const annotationDetails = cacheAnnotations[annotation_uuid];
			const key = getKey(annotationDetails);

			if (key !== null) {
				// @ts-ignore - TODO
				acc[key] = acc[key] || {
					label:       key,
					render:      render ? render(key) : key,
					annotations: []
				};

				acc[key].annotations.push({
					annotation,
					topic: {topic_uuid, type}
				});
			}
		});

		return acc;
	}, {}));

	// Sort top-level groupings in output
	output.sort(sortFn);

	// Sort individual cards within top-level groupings
	output.forEach(section => {
		const sortFn = contentsSortFnByType(cacheAnnotations)[type] || null;
		if (sortFn)
			section.annotations.sort(sortFn);
	});

	return output;
}

/**
 * Creates a virtual outline grouped by source, date, and topic,
 * using the default outline layout defined by the user.
 */
function defaultOutlineSort(outline: mergedOutline2, getAnnotation: getAnnotationFn, render: renderFn | undefined) {
	return outline.map(entry => {
		// @ts-ignore - TODO
		const {topic_uuid, type, annotations} = entry;
		const key = topic_uuid || type;

		return {
			label:       key,
			render:      render ? render(key) : key,
			topic_uuid,
			type,
			annotations: annotations.map(annotation => ({
				annotation,
				topic: {topic_uuid, type}
			}))
		};
	});
}
